import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Badge, Paper } from '@mui/material';
import {
    Person as PersonIcon,
    School as SchoolIcon,
    CardMembership as CertificateIcon,
    History as HistoryIcon,
    Notifications as NotificationIcon,
    Lock as LockIcon,
    Payment as PaymentsDueIcon,
    AccessTime as WaitlistIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import './ProfileNavigation.css';

const ProfileNavigation = ({ activeSection, onSectionChange, profile, isMobile = false }) => {
    const { t } = useTranslation('profile');
    const notifications = profile?.notifications || [];
    const payments = profile?.payments || [];
    const waitlistEntries = profile?.waitlist_entries || [];
    const unreadNotifications = notifications.filter(n => !n.is_read).length;
    const paymentsDue = payments.filter(
        (p) => p.status === 'pending' && Number(p.amount) > 0
    ).length;
    const waitlistCount = waitlistEntries.filter(entry =>
        entry.status === 'waiting' || entry.status === 'pending' || entry.status === 'offered'
    ).length;

    const menuItems = useMemo(() => [
        { value: 'overview', label: t('nav.overview'), icon: <PersonIcon /> },
        { value: 'enrollments', label: t('nav.enrollments'), icon: <SchoolIcon /> },
        {
            value: 'waitlist',
            label: t('nav.waitlist'),
            icon: <Badge badgeContent={waitlistCount} color="info">
                <WaitlistIcon />
            </Badge>
        },
        { value: 'certificates', label: t('nav.certificates'), icon: <CertificateIcon /> },
        {
            value: 'payments',
            label: t('nav.paymentsDue'),
            icon: <Badge badgeContent={paymentsDue} color="error">
                <PaymentsDueIcon />
            </Badge>
        },
        { value: 'password', label: t('nav.password'), icon: <LockIcon /> },
        { value: 'activity', label: t('nav.activity'), icon: <HistoryIcon /> },
        { value: 'email-settings', label: t('nav.emailSettings'), icon: <EmailIcon /> },
        {
            value: 'notifications',
            label: t('nav.notifications'),
            icon: <Badge badgeContent={unreadNotifications} color="error">
                <NotificationIcon />
            </Badge>
        }
    ], [t, waitlistCount, paymentsDue, unreadNotifications]);

    if (isMobile) {
        return (
            <Paper className="profile-navigation mobile" elevation={1}>
                <List component="nav" className="mobile-nav-list">
                    {menuItems.map((item) => (
                        <ListItemButton
                            key={item.value}
                            selected={activeSection === item.value}
                            onClick={() => onSectionChange(item.value)}
                            className="nav-item mobile-nav-item"
                            data-value={item.value}
                        >
                            <ListItemIcon className="mobile-nav-icon">{item.icon}</ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                className="mobile-nav-text"
                            />
                        </ListItemButton>
                    ))}
                </List>
            </Paper>
        );
    }

    return (
        <Paper className="profile-navigation desktop" elevation={1}>
            <List component="nav">
                {menuItems.map((item) => (
                    <ListItemButton
                        key={item.value}
                        selected={activeSection === item.value}
                        onClick={() => onSectionChange(item.value)}
                        className="nav-item"
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} />
                    </ListItemButton>
                ))}
            </List>
        </Paper>
    );
};

export default ProfileNavigation;
